import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCAD } from "@/lib/format";
import { Package, Heart, MessageSquare, Settings } from "lucide-react";

const STATUS_COLOR = {
  PENDING: "bg-[#EFECE6] text-[#5C5A56]",
  PAID: "bg-[#E5EBE8] text-[#2A4B3A]",
  PROCESSING: "bg-[#E5EBE8] text-[#2A4B3A]",
  SHIPPED: "bg-[#F2E8E9] text-[#702A32]",
  DELIVERED: "bg-[#E5EBE8] text-[#2A4B3A]",
  CANCELLED: "bg-[#FBE9E9] text-[#B73B3B]",
};

export default function Account() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  useEffect(() => {
    if (user) api.get("/orders/me").then(({ data }) => setOrders(data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="account-page">
        <div className="overline mb-2">Your account</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Hello, {user.name.split(" ")[0]}.</h1>

        <div className="grid md:grid-cols-4 gap-4 mt-10">
          <StatTile icon={Package} label="Orders" value={orders.length} />
          <StatTile icon={Heart} label="Wishlist" value="—" hint="Coming soon" />
          <StatTile icon={MessageSquare} label="Messages" value="—" hint="Coming soon" />
          <StatTile icon={Settings} label="Settings" value="—" hint="Coming soon" />
        </div>

        <h2 className="font-heading text-2xl mt-12 mb-4">Your orders</h2>
        <div className="space-y-3" data-testid="orders-list">
          {orders.length === 0 ? (
            <div className="surface p-8 text-center text-[#5C5A56]">No orders yet. <Link to="/discover" className="text-[#702A32]">Find something you love</Link>.</div>
          ) : orders.map((o) => (
            <div key={o.order_id} className="surface p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm">{o.order_id}</span>
                  <span className={`tag-pill ${STATUS_COLOR[o.status] || "bg-[#EFECE6]"}`}>{o.status}</span>
                </div>
                <div className="text-xs text-[#5C5A56] mt-1">{new Date(o.created_at).toLocaleDateString()} · {o.lines.length} items</div>
              </div>
              <div className="font-mono">{formatCAD(o.total)}</div>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function StatTile({ icon: Icon, label, value, hint }) {
  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between">
        <span className="overline">{label}</span>
        <Icon className="w-4 h-4 text-[#8A8782]" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-3xl mt-2">{value}</div>
      {hint && <div className="text-xs text-[#8A8782] mt-1">{hint}</div>}
    </div>
  );
}
